'use client'

import { reportHandledException } from '@/lib/error-reporting'
import type { Route } from 'next'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { setActiveContext } from '@kiao-voice/api-client'
import {
  DAY_BOUNDARY_TIME_ZONE,
  type PersonalPaidPlanCode,
  type SubscriptionCycle,
  type UserPlan,
} from '@kiao-voice/core'
import {
  Alert,
  Button,
  KiaoModal,
  PlanCard as PlanCardView,
  SegmentedControl,
  Tag,
} from '@kiao-voice/ui-kit'
import {
  BillingApiError,
  bindCard,
  fetchPersonalInvoiceSettings,
  fetchPersonalSubscription,
  fetchPersonalWallet,
  savePersonalInvoiceSettings,
  fetchSubscriptionUpgradeQuote,
  upgradeSubscription,
  type Plan,
  type SubscriptionPayload,
  type SubscriptionUpgradeQuote,
} from '@/features/billing/api-client'
import {
  maxFileBytesForPlan,
  PLAN_CATALOG,
  YEARLY_DISCOUNT_MONTHS,
  yearlyDiscountPct,
} from '@/features/billing/plan-pricing'
import { FeaturedCampaignSection } from '@/features/promotion-campaign/campaign-section'
import {
  SubscribeCheckoutModal,
  type CheckoutMode,
} from '@/features/billing/subscribe-checkout-modal'
import { BindCardCheckoutModal } from '@/features/billing/bind-card-checkout-modal'
import { TeamSubscribeModal } from '@/features/team/team-subscribe-modal'
import { listContexts, subscribeTeam } from '@/features/team/api-client'
import { fetchMe, readStoredToken } from '@/features/auth/auth-storage'
import { useAuthModal } from '@/features/auth/auth-modal-provider'
import { billingErrorMessage } from '@/features/billing/billing-error-message'
import { paymentRecoveryTradeId } from '@/features/billing/checkout/checkout-outcome'
import {
  billingThreeDsReturnPath,
  writeSubscriptionUpgradeThreeDsIntent,
} from '@/features/billing/three-ds-intent'
import {
  ANALYTICS_EVENTS,
  trackBeginCheckout,
  trackEvent,
  trackSelectPlan,
  trackViewPlans,
  type CommercePlan,
} from '@/lib/analytics'
import { APP_ROUTES } from '@/lib/app-routes'
import { navigateWithinSite } from '@/lib/analytics/script-boundary'

type Cycle = SubscriptionCycle

/**
 * Yearly is the pre-selected segment: it is the cheaper per-month price and the
 * cycle we want buyers to land on. The view_item_list ping below reports the
 * same cycle, so it prices each plan by what this cycle actually bills (the
 * yearly charge), not by the per-month figure the card headlines.
 */
const DEFAULT_CYCLE: Cycle = 'yearly'
type SubscriptionAuthorityState = 'loading' | 'resolved' | 'error'
interface SubscriptionAuthority {
  state: SubscriptionAuthorityState
  token: string | null
}

interface CheckoutTarget {
  planCode: PersonalPaidPlanCode
  cycle: Cycle
  amount: number
  monthlyCredits: number
  /** 'guest' for a signed-out buyer (token-less), 'authed' otherwise. */
  mode: CheckoutMode
}

/** Map an API plan + selected cycle to the analytics ecommerce shape. */
function toCommercePlan(plan: Plan, cycle: Cycle): CommercePlan {
  return {
    code: plan.code,
    name: plan.name,
    priceTwd: cycle === 'yearly' ? plan.yearlyPriceTwd : plan.monthlyPriceTwd,
    cycle,
  }
}

interface Props {
  plans: Plan[]
  /** Test seam — defaults to real Next.js router push. */
  navigate?: <T extends string>(url: Route<T>) => void
  /** Test seam — defaults to reading from localStorage. */
  getTokenFn?: () => string | null
  /** Test seam — skip the TapPay <Script> mount in the checkout modal. */
  skipScript?: boolean
  /** Test seam for the no-card upgrade recovery flow. */
  bindCardFn?: typeof bindCard
  /** Test seam for selecting an owned Team before entering its management page. */
  setActiveContextFn?: typeof setActiveContext
  /** Test seam for Team checkout. */
  subscribeTeamFn?: typeof subscribeTeam
  /** Test seam for the Team invoice defaults loaded before checkout. */
  teamInvoiceLoadFn?: typeof fetchPersonalInvoiceSettings
  /** Test seam for exact upgrade 3DS/recovery correlation. */
  persistUpgradeThreeDsIntentFn?: typeof writeSubscriptionUpgradeThreeDsIntent
}

/**
 * Pricing page per design/pricing.html: pricing-head → SegmentedControl →
 * 3-card plan grid (grouped feat lists) → note → credit-pack band →
 * team band-ink → FAQ grid. The `(public)` layout renders the public `.topnav`
 * (PublicNavbar) and the nested `(marketing)` layout the `.pagefoot` (SiteFooter),
 * so this component only renders <main>.
 */
export function PricingPageClient({
  plans,
  navigate,
  getTokenFn = readStoredToken,
  skipScript,
  bindCardFn = bindCard,
  setActiveContextFn = setActiveContext,
  subscribeTeamFn,
  teamInvoiceLoadFn,
  persistUpgradeThreeDsIntentFn = writeSubscriptionUpgradeThreeDsIntent,
}: Props) {
  const { t, i18n } = useTranslation('web')
  const router = useRouter()
  const authModal = useAuthModal()
  const [cycle, setCycle] = useState<Cycle>(DEFAULT_CYCLE)
  const [selectedPlanCode, setSelectedPlanCode] = useState<Plan['code']>('pro')
  // Checkout happens in-place via a modal for BOTH signed-in (authed) and
  // signed-out (guest, token-less) buyers — no bounce to /signup.
  const [checkout, setCheckout] = useState<CheckoutTarget | null>(null)
  // Set when a guest's email turns out to already have an account: after they
  // OTP-sign-in we auto-resume their checkout for this plan (authed mode).
  const [resumePlan, setResumePlan] = useState<'creator' | 'pro' | null>(null)
  const [resumeTeam, setResumeTeam] = useState(false)
  const [teamCheckoutOpen, setTeamCheckoutOpen] = useState(false)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [subscription, setSubscription] = useState<SubscriptionPayload | null>(null)
  const [subscriptionAuthority, setSubscriptionAuthority] = useState<SubscriptionAuthority>(() => {
    const token = getTokenFn()
    return { state: token ? 'loading' : 'resolved', token }
  })
  const [subscriptionLoadAttempt, setSubscriptionLoadAttempt] = useState(0)
  // Org wallet (儲值金) balance → the authed checkout's offset + card-free path.
  // Null when signed out / flag off / fetch failed (→ full cash price, card).
  const [wallet, setWallet] = useState<{ enabled: boolean; total: number } | null>(null)
  const [upgradeQuote, setUpgradeQuote] = useState<SubscriptionUpgradeQuote | null>(null)
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false)
  const [upgradeLoading, setUpgradeLoading] = useState(false)
  const [upgradeSubmitting, setUpgradeSubmitting] = useState(false)
  const [upgradeError, setUpgradeError] = useState<string | null>(null)
  const [upgradeNeedsCard, setUpgradeNeedsCard] = useState(false)
  const [bindCardOpen, setBindCardOpen] = useState(false)
  const subscriptionRef = useRef<SubscriptionPayload | null>(null)

  // Pre-fetch signed-in account data. Email is a convenience checkout pre-fill;
  // subscription status prevents already-paid users from starting fresh checkout.
  useEffect(() => {
    const token = getTokenFn()
    if (!token) {
      subscriptionRef.current = null
      setSubscription(null)
      setSubscriptionAuthority({ state: 'resolved', token: null })
      return
    }
    let cancelled = false
    setSubscriptionAuthority({ state: 'loading', token })
    if (subscriptionRef.current !== null) {
      subscriptionRef.current = null
      setSubscription(null)
    }
    void fetchMe(token)
      .then((me) => {
        if (!cancelled) setUserEmail(me.user.email)
      })
      .catch((caughtError) => {
        reportHandledException(caughtError, {
          source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
        })
        // Cardholder email is a convenience pre-fill; ignore failures.
      })
    void fetchPersonalWallet()
      .then((w) => {
        if (!cancelled) setWallet({ enabled: w.enabled, total: w.total })
      })
      .catch((caughtError) => {
        reportHandledException(caughtError, {
          source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
        })
        // Wallet is an optional offset; a failure just falls back to cash price.
      })
    void fetchPersonalSubscription()
      .then((response) => {
        if (cancelled) return
        if (subscriptionRef.current !== response.subscription) {
          subscriptionRef.current = response.subscription
          setSubscription(response.subscription)
        }
        setSubscriptionAuthority({ state: 'resolved', token })
      })
      .catch((cause) => {
        reportHandledException(cause, {
          source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
        })
        console.warn('Failed to load pricing subscription status', cause)
        if (!cancelled) {
          if (subscriptionRef.current !== null) {
            subscriptionRef.current = null
            setSubscription(null)
          }
          setSubscriptionAuthority({ state: 'error', token })
        }
      })
    return () => {
      cancelled = true
    }
  }, [getTokenFn, subscriptionLoadAttempt])

  // view_item_list once on mount (the paid plans shown on this page).
  const viewTrackedRef = useRef(false)
  useEffect(() => {
    if (viewTrackedRef.current) return
    viewTrackedRef.current = true
    trackViewPlans(
      plans
        .filter((p) => p.code === 'creator' || p.code === 'pro')
        .map((p) => toCommercePlan(p, DEFAULT_CYCLE))
    )
  }, [plans])

  const go = useCallback(
    <T extends string>(url: Route<T>) => {
      if (navigate) navigate(url)
      else if (url.startsWith('http')) window.location.href = url
      // Into `/app` this page's Clarity must not follow: see script-boundary.
      else navigateWithinSite(url, () => router.push(url))
    },
    [navigate, router]
  )

  const openCheckout = useCallback(
    (planCode: PersonalPaidPlanCode, mode: CheckoutMode) => {
      const plan = plans.find((p) => p.code === planCode)
      if (!plan) return
      const amount = cycle === 'yearly' ? plan.yearlyPriceTwd : plan.monthlyPriceTwd
      setCheckout({ planCode, cycle, amount, monthlyCredits: plan.monthlyCredits, mode })
      // begin_checkout — the in-place payment modal is opening (guest or authed).
      trackBeginCheckout({ code: planCode, name: plan.name, priceTwd: amount, cycle })
    },
    [plans, cycle]
  )

  const openUpgradeConfirmation = useCallback(async () => {
    setUpgradeLoading(true)
    setUpgradeError(null)
    setUpgradeNeedsCard(false)
    try {
      const response = await fetchSubscriptionUpgradeQuote()
      setUpgradeQuote(response.quote)
      setUpgradeDialogOpen(true)
    } catch (cause) {
      reportHandledException(cause, {
        source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
      })
      setUpgradeQuote(null)
      setUpgradeError(t('pricing.upgradeQuoteFailed'))
      setUpgradeDialogOpen(true)
      console.warn('Failed to load subscription upgrade quote', cause)
    } finally {
      setUpgradeLoading(false)
    }
  }, [t])

  const continueAuthedSubscribe = useCallback(
    (planCode: PersonalPaidPlanCode, currentSubscription: SubscriptionPayload | null) => {
      if (hasActiveSubscription(currentSubscription)) {
        if (canUpgradeCreatorToPro(currentSubscription, planCode)) {
          void openUpgradeConfirmation()
          return
        }
        // Not upgradeable (past_due, already Pro, a Team plan): the CTA reads
        // 「管理方案」, so land on the page that actually manages the plan.
        // That page is `/app/account` — `/app/account/billing` is a legacy
        // route whose only job is to redirect here.
        setActiveContextFn('personal')
        go(APP_ROUTES.account)
        return
      }
      openCheckout(planCode, 'authed')
    },
    [go, openCheckout, openUpgradeConfirmation, setActiveContextFn]
  )

  const continueAuthedTeam = useCallback(async () => {
    try {
      // `/organizations/me` renders one selected/seated organization and may
      // return a Team where this user is merely a member, hiding the separate
      // paid Team they own. Contexts enumerates every live Team tenancy, so an
      // owner who is also seated in another organization cannot start a fake
      // duplicate checkout.
      const contexts = await listContexts()
      const ownedTeam = contexts.find(
        (context) => context.mode === 'team' && context.role === 'owner'
      )
      if (ownedTeam) {
        setActiveContextFn(ownedTeam.key)
        go(APP_ROUTES.transcriptions)
        return
      }
      setTeamCheckoutOpen(true)
    } catch (cause) {
      reportHandledException(cause, {
        source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
      })
      console.warn('Failed to refresh Team ownership before checkout', cause)
      // Fail closed: the Team management page can reload authoritative state
      // and offer checkout only when no live Team subscription exists.
      go('/app/account/team')
    }
  }, [go, setActiveContextFn])

  const finishTeamCheckout = useCallback(async () => {
    try {
      // Direct checkout commits the Team subscription before responding. Fetch
      // the post-charge contexts, then select the restored OWNER Team rather
      // than carrying an external member context into the management page.
      const contexts = await listContexts()
      const ownedTeam = contexts.find(
        (context) => context.mode === 'team' && context.role === 'owner'
      )
      if (!ownedTeam) throw new Error('restored Team context is not available')
      setActiveContextFn(ownedTeam.key)
      go(APP_ROUTES.account)
    } catch (cause) {
      reportHandledException(cause, {
        source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
      })
      console.warn('Failed to activate restored Team after checkout', cause)
      // Fail closed: never carry an unrelated external Team context into the
      // management page. The successful checkout stays recoverable on pricing;
      // a later CTA/context refresh can select the restored owner Team.
    }
  }, [go, setActiveContextFn])

  const handleSubscribe = (planCode: PersonalPaidPlanCode) => {
    const plan = plans.find((p) => p.code === planCode)
    // select_item / add_to_cart — a paid plan CTA was clicked.
    if (plan) trackSelectPlan(toCommercePlan(plan, cycle))
    const token = getTokenFn()
    if (!token) {
      // Token-less guest checkout in place — collect email + card and charge
      // via /billing/guest-checkout (account created server-side). Never bounce
      // to /signup or the home page.
      openCheckout(planCode, 'guest')
      return
    }
    // Authentication alone is not enough authority to decide whether a new
    // charge is safe. Until `/billing/subscription` resolves successfully, a
    // paid CTA must remain fail closed rather than treating `null` as no plan.
    if (subscriptionAuthority.state !== 'resolved' || subscriptionAuthority.token !== token) {
      return
    }
    continueAuthedSubscribe(planCode, subscription)
  }

  const confirmUpgrade = async () => {
    if (!upgradeQuote || upgradeSubmitting) return
    let keepSubmitLocked = false
    setUpgradeSubmitting(true)
    setUpgradeError(null)
    try {
      const result = await upgradeSubscription({
        planCode: 'pro',
        expectedAmountTwd: upgradeQuote.amountTwd,
        expectedCashTwd: upgradeQuote.cashTwd,
        expectedPeriodEnd: upgradeQuote.periodEnd,
      })
      if (result.requires3ds) {
        if (!result.paymentUrl) throw new Error('missing 3DS payment URL')
        if (!result.tradeId) throw new Error('missing 3DS trade id')
        persistUpgradeThreeDsIntentFn(result.tradeId)
        // TapPay's hosted 3DS page: an absolute URL minted by our API at
        // request time, so no generated route union can describe it. `go`
        // sends it to `window.location`, not the router.
        go(result.paymentUrl as Route)
        return
      }
      setUpgradeDialogOpen(false)
      setActiveContextFn('personal')
      go(APP_ROUTES.account)
    } catch (cause) {
      reportHandledException(cause, {
        source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
      })
      if (cause instanceof BillingApiError) {
        const body =
          cause.body && typeof cause.body === 'object'
            ? (cause.body as { error?: string; quote?: SubscriptionUpgradeQuote })
            : null
        if (cause.status === 409 && body?.error === 'upgrade_quote_changed' && body.quote) {
          setUpgradeQuote(body.quote)
          setUpgradeError(t('pricing.upgradeQuoteChanged'))
          return
        }
        const recoveryTradeId = paymentRecoveryTradeId(cause)
        if (recoveryTradeId !== null) {
          keepSubmitLocked = true
          persistUpgradeThreeDsIntentFn(recoveryTradeId)
          setUpgradeError(t('billing.payError.recoveryPending'))
          go(
            billingThreeDsReturnPath({
              product: 'subscription_upgrade',
              tradeId: recoveryTradeId,
              targetPlanCode: 'pro',
            })
          )
          return
        }
        if (body?.error === 'no_card_on_file') {
          setUpgradeError(t('pricing.upgradeNoCard'))
          setUpgradeNeedsCard(true)
          return
        }
        if (body?.error === 'upgrade_not_available') {
          setUpgradeError(t('pricing.upgradeNotAvailable'))
          return
        }
      }
      setUpgradeError(billingErrorMessage(cause, t, 'pricing.upgradeFailed'))
    } finally {
      if (!keepSubmitLocked) setUpgradeSubmitting(false)
    }
  }

  const openUpgradeCardBinding = () => {
    setUpgradeDialogOpen(false)
    setBindCardOpen(true)
  }

  const restoreUpgradeConfirmation = () => {
    setBindCardOpen(false)
    if (upgradeQuote) setUpgradeDialogOpen(true)
  }

  const handleUpgradeCardBound = () => {
    setUpgradeNeedsCard(false)
    setUpgradeError(null)
    restoreUpgradeConfirmation()
  }

  // Guest entered an email that already has an account → no charge happened.
  // Pivot to in-place OTP sign-in (prefilled), then resume the authed checkout.
  // When the card WAS charged (`paid`), there is nothing to resume: say so, and
  // sign in toward the account the purchase landed on.
  const handleRequiresSignIn = useCallback(
    (email: string, planCode: PersonalPaidPlanCode, paid: boolean) => {
      if (paid) {
        authModal.open({
          redirectTo: APP_ROUTES.account,
          prefillEmail: email,
          notice: t('pricing.guestPaidSignInNotice'),
        })
        return
      }
      setResumePlan(planCode)
      authModal.open({
        redirectTo: null,
        prefillEmail: email,
        notice: t('pricing.guestExistingAccountNotice'),
      })
    },
    [authModal, t]
  )

  // Once OTP closes, the localStorage token has changed without remounting this
  // page. Rehydrate the account before deciding how to continue: an existing
  // Plus member choosing Pro must enter the prorated upgrade flow, never a
  // normal subscribe request that the API would deduplicate as a false success.
  useEffect(() => {
    if (!resumePlan || authModal.isOpen) return
    const token = getTokenFn()
    if (!token) {
      setResumePlan(null)
      return
    }
    let cancelled = false
    void fetchMe(token)
      .then((me) => {
        if (!cancelled) setUserEmail(me.user.email)
      })
      .catch((caughtError) => {
        reportHandledException(caughtError, {
          source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
        })
        // Cardholder email is only a convenience pre-fill.
      })
    void fetchPersonalWallet()
      .then((response) => {
        if (!cancelled) setWallet({ enabled: response.enabled, total: response.total })
      })
      .catch((caughtError) => {
        reportHandledException(caughtError, {
          source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
        })
        // Wallet remains an optional checkout offset.
      })
    void fetchPersonalSubscription()
      .then((response) => {
        if (cancelled) return
        subscriptionRef.current = response.subscription
        setSubscription(response.subscription)
        setSubscriptionAuthority({ state: 'resolved', token })
        continueAuthedSubscribe(resumePlan, response.subscription)
      })
      .catch((cause) => {
        reportHandledException(cause, {
          source: 'app/(public)/(marketing)/pricing/pricing-page-client.tsx',
        })
        if (cancelled) return
        console.warn('Failed to refresh pricing subscription after sign-in', cause)
        setSubscriptionAuthority({ state: 'error', token })
        // Fail closed: never start a potentially duplicate charge when we
        // cannot determine whether this account already owns a subscription.
        go('/app/account')
      })
      .finally(() => {
        if (!cancelled) setResumePlan(null)
      })
    return () => {
      cancelled = true
    }
  }, [resumePlan, authModal.isOpen, getTokenFn, continueAuthedSubscribe, go])

  useEffect(() => {
    if (!resumeTeam || authModal.isOpen) return
    if (getTokenFn()) void continueAuthedTeam()
    setResumeTeam(false)
  }, [resumeTeam, authModal.isOpen, getTokenFn, continueAuthedTeam])

  const handleCreditPack = () => {
    // Credit-pack purchase lives on the billing page; route there in-app
    // instead of dumping signed-out users to /signup → home. Signed-out → open
    // OTP in place, then land on the pack intent.
    const packHref = '/app/account/billing?intent=pack'
    if (getTokenFn()) {
      go(packHref)
      return
    }
    authModal.open({ redirectTo: packHref })
  }

  const activePlanCode = checkout?.planCode ?? selectedPlanCode
  const currentToken = getTokenFn()
  const isSignedIn = Boolean(currentToken)
  const currentSubscriptionAuthorityState: SubscriptionAuthorityState = !currentToken
    ? 'resolved'
    : subscriptionAuthority.token === currentToken
      ? subscriptionAuthority.state
      : 'loading'
  const subscriptionIsActive = hasActiveSubscription(subscription)
  const subscriptionIsComplimentary = subscription?.fundingSource === 'complimentary'
  /**
   * Nothing on this page can change a paying personal subscriber's billing
   * cycle: their only action here is the same-cycle Plus → Pro upgrade, or
   * 「管理方案」. Showing them the other cycle's prices therefore advertises a
   * number they cannot buy, so the switch is pinned to the cycle they hold —
   * for every paying personal subscriber, not only the upgradeable ones, since
   * a Pro or past_due subscriber cannot switch cycle either. Visitors, Free
   * users and complimentary users keep it.
   *
   * Accepted side effect: the same switch prices the Team band, so a locked
   * subscriber also sees Team quoted in their own cycle.
   */
  const lockedCycle: Cycle | null =
    currentSubscriptionAuthorityState === 'resolved' &&
    subscription &&
    subscription.planCode !== 'team' &&
    subscriptionIsActive
      ? subscription.cycle
      : null
  useEffect(() => {
    if (lockedCycle) setCycle(lockedCycle)
  }, [lockedCycle])
  const personalPlans = plans.filter(
    (plan): plan is Plan & { code: UserPlan } => plan.code !== 'team'
  )
  const creatorPlan = plans.find((p) => p.code === 'creator')
  const teamPlan =
    plans.find((p) => p.code === 'team') ?? PLAN_CATALOG.find((p) => p.code === 'team')!
  const yearlyPct = creatorPlan
    ? yearlyDiscountPct(creatorPlan.monthlyPriceTwd, creatorPlan.yearlyPriceTwd)
    : 0
  const teamYearlyPct = yearlyDiscountPct(teamPlan.monthlyPriceTwd, teamPlan.yearlyPriceTwd)

  const handleTeamSubscribe = () => {
    trackEvent(ANALYTICS_EVENTS.pricingTeamCtaClick, { cycle })
    if (getTokenFn()) {
      void continueAuthedTeam()
      return
    }
    setResumeTeam(true)
    authModal.open({ redirectTo: null })
  }

  return (
    <main id="content">
      <section className="container pricing-head">
        <p className="eyebrow">{t('pricing.eyebrow')}</p>
        <h1>{t('pricing.heading')}</h1>
        <p className="lead">{t('pricing.subhead')}</p>
      </section>

      <section className="container">
        <div className="flex justify-center mb-[var(--gap-lg)]">
          <SegmentedControl
            aria-label={t('pricing.billingCycleAria')}
            value={cycle}
            disabled={lockedCycle !== null}
            variant="ink"
            options={[
              { value: 'monthly', label: t('pricing.cycleMonthly') },
              { value: 'yearly', label: t('pricing.cycleYearly', { pct: yearlyPct }) },
            ]}
            onValueChange={(nextCycle) => {
              trackEvent(ANALYTICS_EVENTS.pricingCycleToggle, { cycle: nextCycle })
              setCycle(nextCycle)
            }}
          />
        </div>

        {isSignedIn && currentSubscriptionAuthorityState === 'error' ? (
          <div className="mb-5">
            <Alert>{t('pricing.subscriptionStatusFailed')}</Alert>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => {
                setSubscriptionAuthority({ state: 'loading', token: currentToken })
                setSubscriptionLoadAttempt((attempt) => attempt + 1)
              }}
            >
              {t('pricing.subscriptionStatusRetry')}
            </Button>
          </div>
        ) : null}

        <div className="grid grid-cols-1 items-stretch gap-[var(--gap-md)] min-[921px]:grid-cols-3">
          {personalPlans.map((plan) => (
            <PlanCard
              key={plan.code}
              plan={plan}
              cycle={cycle}
              isPending={
                plan.code !== 'free' &&
                isSignedIn &&
                currentSubscriptionAuthorityState === 'loading'
              }
              ctaDisabled={
                plan.code !== 'free' &&
                isSignedIn &&
                currentSubscriptionAuthorityState !== 'resolved'
              }
              selected={activePlanCode === plan.code}
              ctaLabel={
                plan.code !== 'free' && currentSubscriptionAuthorityState === 'loading'
                  ? t('pricing.ctaCheckingSubscription')
                  : plan.code !== 'free' && currentSubscriptionAuthorityState === 'error'
                    ? t('pricing.subscriptionStatusUnavailable')
                    : plan.code !== 'free' && subscriptionIsComplimentary
                      ? t('pricing.ctaStartPaidNow')
                      : plan.code !== 'free' && subscriptionIsActive
                        ? plan.code === subscription?.planCode
                          ? t('pricing.ctaCurrentPlan')
                          : canUpgradeCreatorToPro(subscription, plan.code)
                            ? t('pricing.ctaUpgradePlan')
                            : t('pricing.ctaManagePlan')
                        : undefined
              }
              onSelect={() => {
                // Card-body click = considering/comparing this plan (the CTA
                // stops propagation, so this never double-fires with select_item).
                trackEvent(ANALYTICS_EVENTS.pricingPlanCardClick, { plan: plan.code, cycle })
                setSelectedPlanCode(plan.code)
              }}
              onCtaClick={() => {
                if (plan.code === 'free') {
                  // Free has nothing to charge. The trial entry sends a signed-in
                  // member to their workspace and everyone else to the free trial.
                  trackEvent(ANALYTICS_EVENTS.pricingFreeCtaClick)
                  go('/batch-transcription')
                } else {
                  handleSubscribe(plan.code)
                }
              }}
            />
          ))}
        </div>
        <p className="note text-center mt-[var(--gap-md)]">{t('pricing.howCreditsItem2')}</p>
      </section>

      {/* The SAME section as the home page, not a bespoke row — one component,
          one set of states to keep correct. Below the plan cards and above the
          credit packs, per the design: this is the "try it first" frame, NOT a
          discount, so it must not sit beside the prices or feed checkout.
          Hidden only for someone already PAYING — they are here to look at
          plans and a panel saying they have one is noise. A campaign gift that
          is counting down still shows: 「還剩 N 天」beside the prices is the
          conversion moment, not clutter. */}
      <FeaturedCampaignSection
        surface="pricing"
        sectionId="trial"
        className="campaign-pricing"
        hideForPaidSubscribers
      />

      <section className="band mt-[var(--gap-xl)] py-[var(--gap-xl)]">
        <div className="container pack-row">
          <div>
            <p className="eyebrow">{t('pricing.creditPackHeading')}</p>
            <h2>{t('pricing.creditPackTitle')}</h2>
            <p className="lead">{t('pricing.creditPackDescription')}</p>
          </div>
          <Button
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.pricingCreditPackCtaClick)
              handleCreditPack()
            }}
          >
            {t('pricing.creditPackCta')}
          </Button>
        </div>
      </section>

      <section className="band-ink py-[var(--gap-xl)] scroll-mt-[var(--scroll-anchor)]" id="team">
        <div className="container pack-row">
          <div>
            <p className="eyebrow">{t('pricing.teamHeading')}</p>
            <h2>{t('pricing.teamTitle')}</h2>
            <p className="lead">{t('pricing.teamDescription')}</p>
          </div>
          <Button variant="inverse" onClick={handleTeamSubscribe}>
            {t('pricing.teamCta')}
          </Button>
        </div>
        <div className="container team-points">
          <div className="tp">
            <p className="tp-h">{t('pricing.teamSeatBillingTitle')}</p>
            <p className="tp-body">{t('pricing.teamSeatBillingBody')}</p>
          </div>
          <div className="tp">
            <p className="tp-h">{t('pricing.teamCreatorSeatTitle')}</p>
            <p className="tp-body">{t('pricing.teamCreatorSeatBody')}</p>
          </div>
          <div className="tp">
            <p className="tp-h">{t('pricing.teamSharedCreditsTitle')}</p>
            <p className="tp-body">{t('pricing.teamSharedCreditsBody')}</p>
          </div>
        </div>
        <p className="container meta mt-[var(--gap-lg)] opacity-[var(--opacity-ink-subtle)]">
          {/* Follows the toggle above: the Team CTA carries that same cycle into
              the checkout modal, so a fixed headline price would advertise one
              number and charge another. */}
          {t(cycle === 'yearly' ? 'pricing.teamPerSeatYearly' : 'pricing.teamPerSeatMonthly', {
            yearly: teamPlan.yearlyPriceTwd.toLocaleString('en-US'),
            monthly: teamPlan.monthlyPriceTwd.toLocaleString('en-US'),
            // Amortised, not a price anyone is charged — the monthly PLAN price
            // is `monthlyPriceTwd`, which the FAQ below quotes.
            monthlyEquivalent: Math.round(teamPlan.yearlyPriceTwd / 12).toLocaleString('en-US'),
            pct: teamYearlyPct,
          })}
        </p>
      </section>

      <section className="container py-[var(--gap-xl)]" id="faq">
        <div className="max-w-[var(--measure-copy)] mb-[var(--gap-lg)]">
          <p className="eyebrow">{t('pricing.faqEyebrow')}</p>
          <h2>{t('pricing.faqTitle')}</h2>
        </div>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>{t('pricing.faqCreditsQ')}</h3>
            <p>{t('pricing.faqCreditsA')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faqDifferenceQ')}</h3>
            <p>{t('pricing.faqDifferenceA')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faqRunOutQ')}</h3>
            <p>{t('pricing.faqRunOutA')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faqFileLengthQ')}</h3>
            <p>{t('pricing.faqFileLengthA')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faqRetentionQ')}</h3>
            <p>{t('pricing.faqRetentionA')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faqCancelQ')}</h3>
            <p>{t('pricing.faqCancelA')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faqYearlyQ')}</h3>
            <p>{t('pricing.faqYearlyA', { months: YEARLY_DISCOUNT_MONTHS, pct: yearlyPct })}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faqSecurityQ')}</h3>
            <p>{t('pricing.faqSecurityA')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faqTeamQ')}</h3>
            <p>
              {t('pricing.faqTeamA', {
                yearly: teamPlan.yearlyPriceTwd.toLocaleString('en-US'),
                monthly: teamPlan.monthlyPriceTwd.toLocaleString('en-US'),
              })}{' '}
              <Link href="/app/account/team">{t('pricing.faqTeamLink')}</Link>
            </p>
          </div>
        </div>
      </section>

      {checkout ? (
        <SubscribeCheckoutModal
          open
          planCode={checkout.planCode}
          cycle={checkout.cycle}
          amount={checkout.amount}
          monthlyCredits={checkout.monthlyCredits}
          userEmail={checkout.mode === 'guest' ? null : userEmail}
          mode={checkout.mode}
          wallet={checkout.mode === 'guest' ? null : wallet}
          replacingComplimentary={
            checkout.mode === 'authed' && subscription?.fundingSource === 'complimentary'
          }
          onRequiresSignIn={(email, paid) => handleRequiresSignIn(email, checkout.planCode, paid)}
          onClose={() => setCheckout(null)}
          onSuccess={() => {
            setActiveContextFn('personal')
            go(APP_ROUTES.account)
          }}
          invoiceLoadFn={fetchPersonalInvoiceSettings}
          invoiceSaveFn={savePersonalInvoiceSettings}
          skipScript={skipScript}
        />
      ) : null}
      {teamCheckoutOpen ? (
        <TeamSubscribeModal
          open
          pricing={{
            monthlyPriceTwd: teamPlan.monthlyPriceTwd,
            yearlyPriceTwd: teamPlan.yearlyPriceTwd,
            minSeats: 1,
          }}
          initialCycle={cycle}
          resetKey="pricing-team"
          userEmail={userEmail}
          getTokenFn={getTokenFn}
          onClose={() => setTeamCheckoutOpen(false)}
          onSuccess={() => void finishTeamCheckout()}
          subscribeFn={subscribeTeamFn}
          invoiceLoadFn={teamInvoiceLoadFn}
          skipScript={skipScript}
        />
      ) : null}
      {bindCardOpen ? (
        <BindCardCheckoutModal
          open
          userEmail={userEmail}
          onClose={restoreUpgradeConfirmation}
          onSuccess={handleUpgradeCardBound}
          getTokenFn={getTokenFn}
          bindCardFn={bindCardFn}
          skipScript={skipScript}
        />
      ) : null}
      <KiaoModal
        open={upgradeDialogOpen}
        onClose={() => {
          if (!upgradeSubmitting) setUpgradeDialogOpen(false)
        }}
        title={t('pricing.upgradeConfirmTitle')}
        ariaLabel={t('pricing.upgradeConfirmTitle')}
        closeLabel={t('common.cancel')}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={upgradeSubmitting}
              onClick={() => setUpgradeDialogOpen(false)}
            >
              {t('common.cancel')}
            </Button>
            {upgradeNeedsCard ? (
              <Button disabled={upgradeSubmitting} onClick={openUpgradeCardBinding}>
                {t('pricing.upgradeAddCardCta')}
              </Button>
            ) : (
              <Button
                disabled={!upgradeQuote || upgradeSubmitting}
                onClick={() => void confirmUpgrade()}
              >
                {upgradeSubmitting
                  ? t('pricing.upgradeSubmitting')
                  : t('pricing.upgradeConfirmCta')}
              </Button>
            )}
          </>
        }
      >
        {upgradeQuote ? (
          <>
            <p>
              {t('pricing.upgradeProratedCharge', {
                amount: upgradeQuote.amountTwd.toLocaleString('en-US'),
              })}
            </p>
            {upgradeQuote.walletTwd > 0 ? (
              <p>
                {t('pricing.upgradeWalletBreakdown', {
                  wallet: upgradeQuote.walletTwd.toLocaleString('en-US'),
                  cash: upgradeQuote.cashTwd.toLocaleString('en-US'),
                })}
              </p>
            ) : null}
            {/* The SERVER decides which cycle is being bought, so the copy
                follows `quote.cycle` rather than this page's switch: a stale or
                mid-flight switch must never describe the charge. */}
            <p>
              {t(
                upgradeQuote.cycle === 'yearly'
                  ? 'pricing.upgradeCreditsYearly'
                  : 'pricing.upgradeCredits',
                {
                  credits: upgradeQuote.additionalCredits.toLocaleString('en-US', {
                    maximumFractionDigits: 1,
                  }),
                }
              )}
            </p>
            <p>
              {t(
                upgradeQuote.cycle === 'yearly'
                  ? 'pricing.upgradeAnchorUnchangedYearly'
                  : 'pricing.upgradeAnchorUnchanged',
                {
                  date: new Date(upgradeQuote.periodEnd).toLocaleDateString(i18n.language, {
                    timeZone: DAY_BOUNDARY_TIME_ZONE,
                  }),
                }
              )}
            </p>
          </>
        ) : null}
        {upgradeLoading ? <p>{t('common.loading')}</p> : null}
        {upgradeError ? <Alert>{upgradeError}</Alert> : null}
      </KiaoModal>
    </main>
  )
}

interface PlanCardProps {
  plan: Plan
  cycle: Cycle
  isPending: boolean
  ctaDisabled: boolean
  selected: boolean
  ctaLabel?: string
  onCtaClick: () => void
  onSelect: () => void
}

const formatTwd = (amount: number) => `NT$${amount.toLocaleString('en-US')}`

const hasActiveSubscription = (subscription: SubscriptionPayload | null) =>
  subscription?.fundingSource !== 'complimentary' &&
  (subscription?.status === 'active' ||
    subscription?.status === 'trialing' ||
    subscription?.status === 'past_due')

const canUpgradeCreatorToPro = (subscription: SubscriptionPayload | null, planCode: Plan['code']) =>
  subscription?.fundingSource !== 'complimentary' &&
  (subscription?.status === 'active' || subscription?.status === 'trialing') &&
  subscription.planCode === 'creator' &&
  planCode === 'pro'

function PlanCard({
  plan,
  cycle,
  isPending,
  ctaDisabled,
  selected,
  ctaLabel,
  onSelect,
  onCtaClick,
}: PlanCardProps) {
  const { t } = useTranslation('web')
  const isFree = plan.code === 'free'
  const featured = plan.code === 'pro'
  // Yearly shows the per-month equivalent (yearly price ÷ 12).
  const displayPriceTwd =
    cycle === 'yearly' ? Math.round(plan.yearlyPriceTwd / 12) : plan.monthlyPriceTwd

  const planLabel = isFree
    ? t('pricing.free')
    : plan.code === 'creator'
      ? t('pricing.creator')
      : t('pricing.pro')

  const sub = isFree
    ? t('pricing.descFree')
    : cycle === 'monthly'
      ? plan.code === 'creator'
        ? t('pricing.descCreator')
        : t('pricing.descPro')
      : t('pricing.billedYearly', {
          amount: displayPriceTwd.toLocaleString('en-US'),
          yearly: plan.yearlyPriceTwd.toLocaleString('en-US'),
        })

  // The single-file cap line is derived from the plan's OWN `maxFileMinutes`,
  // never from a per-plan hardcoded string. The hardcoded variants (one per
  // plan) silently drifted from the catalog: the copy said one number while
  // `plan.maxFileMinutes` said another, and no test could catch it because the
  // assertions matched the hardcoded string rather than the plan data.
  // Whole hours read better than "180 minutes", hence the two-key split.
  const capLine =
    plan.maxFileMinutes % 60 === 0 && plan.maxFileMinutes >= 60
      ? t('pricing.maxFileHours', { hours: plan.maxFileMinutes / 60 })
      : t('pricing.maxFileMinutes', { minutes: plan.maxFileMinutes })
  const fileSizeLine = t('pricing.maxFileBytes', {
    mb: maxFileBytesForPlan(plan.code) / (1024 * 1024),
  })

  // Grouped features per design/pricing.html: each plan shows a Voice-input
  // group then a Transcripts group, rendered as <li class="feat-group"> headers
  // followed by the per-group lines.
  const featureGroups = isFree
    ? [
        { heading: t('pricing.featGroupVoice'), lines: [t('pricing.voiceFree')] },
        {
          heading: t('pricing.featGroupTranscript'),
          lines: [
            t('pricing.featFreeCredits', { count: plan.monthlyCredits }),
            capLine,
            fileSizeLine,
            t('pricing.featFreeFormats'),
            t('pricing.featRetentionFree'),
          ],
        },
      ]
    : plan.code === 'creator'
      ? [
          {
            heading: t('pricing.featGroupVoice'),
            lines: [t('pricing.voiceUnlimited'), t('pricing.featCreatorDictionary')],
          },
          {
            heading: t('pricing.featGroupTranscript'),
            lines: [
              t('pricing.featCreatorCreditsDuration'),
              capLine,
              fileSizeLine,
              t('pricing.featRetentionPaid'),
            ],
          },
        ]
      : [
          {
            heading: t('pricing.featGroupVoice'),
            lines: [t('pricing.voiceUnlimited'), t('pricing.featCreatorDictionary')],
          },
          {
            heading: t('pricing.featGroupTranscript'),
            lines: [
              t('pricing.featProCreditsDuration'),
              capLine,
              fileSizeLine,
              t('pricing.featProAudience'),
              t('pricing.featRetentionPaid'),
            ],
          },
        ]

  return (
    <PlanCardView
      name={planLabel}
      featured={featured}
      selected={selected}
      price={formatTwd(displayPriceTwd)}
      per={isFree ? undefined : `/${t('pricing.perMonth')}`}
      sub={sub}
      badge={featured ? <Tag fill>{t('pricing.mostPopular')}</Tag> : undefined}
      featureGroups={featureGroups}
      onClick={(event) => {
        // Card body selection is visual only; CTA owns navigation/checkout.
        if (isPending) return
        if ((event.target as HTMLElement).closest('button')) return
        onSelect()
      }}
      cta={
        <Button
          variant={selected ? 'primary' : 'secondary'}
          disabled={isPending || ctaDisabled}
          onClick={(event) => {
            event.stopPropagation()
            onCtaClick()
          }}
        >
          {isPending
            ? (ctaLabel ?? t('pricing.ctaStartingCheckout'))
            : (ctaLabel ?? (isFree ? t('pricing.ctaFree') : t('pricing.ctaChoose')))}
        </Button>
      }
    />
  )
}
